import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, FileText, Shield, BadgeCheck, IndianRupee, AlertTriangle, Settings, Hash, CircleDot, Calendar, Users, CreditCard, CheckCircle, Car } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import StatusBadge from '../common/StatusBadge';
import { vehiclesAPI } from '../../services/api';
import { COLORS, formatLabel } from '../../utils/constants';

const C = COLORS;

const InfoRow = ({ icon, label, value, badge }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: `1px solid ${C.g100}` }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.g100, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: C.g500 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <p style={{ margin: 0, fontSize: 12, color: C.g400, fontWeight: 500 }}>{label}</p>
      <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 600, color: C.g800, wordBreak: 'break-word' }}>{value || '—'}</p>
    </div>
    {badge && <StatusBadge status={badge} />}
  </div>
);

const Card = ({ children, style }) => <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, padding: 24, ...style }}>{children}</div>;

const VehicleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [tab, setTab] = useState('rc');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vehiclesAPI.getById(id)
      .then(r => setV(r.data))
      .catch(() => navigate('/garage'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <AppLayout title="Loading..."><p>Loading vehicle details...</p></AppLayout>;
  if (!v) return <AppLayout title="Not Found"><p>Vehicle not found.</p></AppLayout>;

  const tabs = [
    { id: 'rc', label: 'RC Details', icon: <FileText size={16} /> },
    { id: 'insurance', label: 'Insurance', icon: <Shield size={16} /> },
    { id: 'pucc', label: 'PUCC', icon: <BadgeCheck size={16} /> },
    { id: 'loan', label: 'Loan / HP', icon: <IndianRupee size={16} /> },
    { id: 'challans', label: 'Challans', icon: <AlertTriangle size={16} />, badge: v.challans?.filter(c => c.status === 'unpaid').length },
    { id: 'service', label: 'Service Log', icon: <Settings size={16} /> },
  ];

  const unpaidTotal = v.challans?.filter(c => c.status === 'unpaid').reduce((s, c) => s + parseInt(c.amount?.replace(/[^\d]/g, '') || 0), 0) || 0;

  return (
    <AppLayout title="Vehicle Details">
      <button onClick={() => navigate('/garage')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: C.g500, marginBottom: 16, fontFamily: 'inherit', padding: 0 }}>
        <ChevronLeft size={18} /> Back to Vehicles
      </button>

      {/* Header Card */}
      <Card style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ background: `linear-gradient(135deg, ${C.primary}, #6366F1)`, padding: '28px 28px 24px', color: '#fff', position: 'relative' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, position: 'relative' }}>
            <div>
              <p style={{ margin: '0 0 4px', fontSize: 13, opacity: 0.7 }}>{v.maker || 'Unknown Maker'}</p>
              <h2 style={{ margin: '0 0 4px', fontSize: 26, fontWeight: 800 }}>{v.name}</h2>
              <p style={{ margin: 0, fontSize: 15, opacity: 0.85 }}>{v.model || ''}</p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: '10px 18px' }}>
              <p style={{ margin: 0, fontSize: 11, opacity: 0.7 }}>Registration No.</p>
              <p style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, letterSpacing: 1.5, fontFamily: 'monospace' }}>{v.plate_number}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { l: 'Color', v: v.color }, { l: 'Fuel', v: formatLabel(v.fuel_type) }, { l: 'Year', v: v.year },
              { l: 'Seating', v: v.seating }, { l: 'CC', v: v.cc || 'Electric' }, { l: 'Body', v: v.body_type },
            ].map((d, i) => (
              <div key={i} style={{ minWidth: 70 }}>
                <p style={{ margin: 0, fontSize: 11, opacity: 0.6 }}>{d.l}</p>
                <p style={{ margin: '2px 0 0', fontSize: 14, fontWeight: 600 }}>{d.v || '—'}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Status */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          {[
            { label: 'RC', value: v.rc?.rc_status || 'N/A', ok: v.rc?.rc_status === 'valid' },
            { label: 'Insurance', value: v.insurance?.status || 'N/A', ok: v.insurance?.status === 'active' },
            { label: 'PUCC', value: v.pucc?.status || 'N/A', ok: ['valid', 'exempt'].includes(v.pucc?.status) },
            { label: 'Fitness', value: v.fitness?.status || 'N/A', ok: v.fitness?.status === 'valid' },
            { label: 'Loan', value: v.loan ? v.loan.status : 'No Loan', ok: !v.loan || v.loan.status === 'active' },
            { label: 'Challans', value: (v.challans?.filter(c => c.status === 'unpaid').length || 0) > 0 ? `${v.challans.filter(c => c.status === 'unpaid').length} Unpaid` : 'Clear', ok: !(v.challans?.filter(c => c.status === 'unpaid').length > 0) },
          ].map((s, i) => (
            <div key={i} style={{ padding: '16px 20px', borderRight: i < 5 ? `1px solid ${C.g100}` : 'none', borderBottom: `1px solid ${C.g100}` }}>
              <p style={{ margin: 0, fontSize: 11, color: C.g400, fontWeight: 500 }}>{s.label}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                {s.ok ? <CheckCircle size={14} style={{ color: C.success }} /> : <AlertTriangle size={14} style={{ color: C.danger }} />}
                <span style={{ fontSize: 14, fontWeight: 700, color: s.ok ? C.success : C.danger }}>{formatLabel(s.value)}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
            border: `1px solid ${tab === t.id ? C.primary : C.g200}`, borderRadius: 10,
            background: tab === t.id ? C.primaryGhost : '#fff', cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t.id ? 600 : 400, color: tab === t.id ? C.primary : C.g600,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            {t.icon} {t.label}
            {t.badge > 0 && <span style={{ background: C.danger, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10 }}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Card>
        {tab === 'rc' && (
          <div>
            <h4 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: C.g900 }}>Registration Certificate</h4>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: C.g400 }}>Vehicle RC details as per RTO records</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <div style={{ paddingRight: 24 }}>
                <InfoRow icon={<Hash size={16} />} label="Chassis Number" value={v.chassis_number} />
                <InfoRow icon={<CircleDot size={16} />} label="Engine Number" value={v.engine_number} />
                <InfoRow icon={<Calendar size={16} />} label="Registration Date" value={v.reg_date} />
                <InfoRow icon={<FileText size={16} />} label="RC Type" value={v.rc?.rc_type} badge={v.rc?.rc_status} />
                <InfoRow icon={<Calendar size={16} />} label="RC Valid Till" value={v.rc?.valid_till} />
              </div>
              <div style={{ paddingLeft: 24, borderLeft: `1px solid ${C.g100}` }}>
                <InfoRow icon={<Users size={16} />} label="Owner Name" value={v.owner_name} />
                <InfoRow icon={<Users size={16} />} label="Father/Husband Name" value={v.father_name} />
                <InfoRow icon={<FileText size={16} />} label="RTO Authority" value={v.reg_authority} />
                <InfoRow icon={<CreditCard size={16} />} label="Tax Paid" value={v.tax_paid ? `${v.tax_paid} (Valid till ${v.tax_valid_till})` : '—'} />
                <InfoRow icon={<Shield size={16} />} label="Blacklisted" value={v.blacklisted ? 'Yes' : 'No'} />
              </div>
            </div>
            {v.address && (
              <div style={{ marginTop: 16, padding: 16, background: C.g50, borderRadius: 12 }}>
                <p style={{ margin: 0, fontSize: 12, color: C.g400 }}>Registered Address</p>
                <p style={{ margin: '4px 0 0', fontSize: 14, color: C.g700 }}>{v.address}</p>
              </div>
            )}
          </div>
        )}

        {tab === 'insurance' && (
          <div>
            <h4 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: C.g900 }}>Insurance Details</h4>
            {v.insurance ? (
              <>
                <div style={{ background: v.insurance.status === 'active' ? C.successLight : C.dangerLight, borderRadius: 14, padding: 20, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 12, color: v.insurance.status === 'active' ? C.success : C.danger }}>Provider</p>
                      <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: v.insurance.status === 'active' ? '#065F46' : '#991B1B' }}>{v.insurance.provider}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: 12, color: v.insurance.status === 'active' ? C.success : C.danger }}>Premium</p>
                      <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: v.insurance.status === 'active' ? '#065F46' : '#991B1B' }}>{v.insurance.premium}</p>
                    </div>
                  </div>
                </div>
                <InfoRow icon={<Hash size={16} />} label="Policy Number" value={v.insurance.policy_no} />
                <InfoRow icon={<Calendar size={16} />} label="Valid From" value={v.insurance.valid_from} />
                <InfoRow icon={<Calendar size={16} />} label="Valid Till" value={v.insurance.valid_till} badge={v.insurance.status} />
              </>
            ) : <EmptyState text="No insurance records" />}
          </div>
        )}

        {tab === 'pucc' && (
          <div>
            <h4 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: C.g900 }}>PUCC Certificate</h4>
            {v.pucc ? (
              <>
                <InfoRow icon={<Hash size={16} />} label="Certificate Number" value={v.pucc.cert_no} />
                <InfoRow icon={<Calendar size={16} />} label="Valid From" value={v.pucc.valid_from} />
                <InfoRow icon={<Calendar size={16} />} label="Valid Till" value={v.pucc.valid_till} badge={v.pucc.status} />
                <InfoRow icon={<BadgeCheck size={16} />} label="Test Result" value={formatLabel(v.pucc.result)} badge={v.pucc.result === 'pass' ? 'pass' : v.pucc.result === 'na' ? 'exempt' : undefined} />
              </>
            ) : <EmptyState text="No PUCC records" />}
          </div>
        )}

        {tab === 'loan' && (
          <div>
            <h4 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: C.g900 }}>Loan / Hypothecation</h4>
            {v.loan ? (
              <>
                <div style={{ background: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 12, color: C.warning }}>Financed By</p>
                      <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 800, color: '#92400E' }}>{v.loan.bank}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: 12, color: C.warning }}>Monthly EMI</p>
                      <p style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: '#92400E' }}>{v.loan.emi}</p>
                    </div>
                  </div>
                </div>
                <InfoRow icon={<IndianRupee size={16} />} label="Loan Amount" value={v.loan.loan_amount} />
                <InfoRow icon={<Calendar size={16} />} label="Tenure" value={v.loan.tenure} />
                <InfoRow icon={<Calendar size={16} />} label="Start Date" value={v.loan.start_date} />
                <InfoRow icon={<Calendar size={16} />} label="End Date" value={v.loan.end_date} badge={v.loan.status} />
              </>
            ) : <EmptyState text="No active loan" icon={<CheckCircle size={48} style={{ color: C.success }} />} sub="This vehicle has no finance records." />}
          </div>
        )}

        {tab === 'challans' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <h4 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.g900 }}>Traffic Challans</h4>
              {unpaidTotal > 0 && (
                <div style={{ background: C.dangerLight, borderRadius: 12, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertTriangle size={16} style={{ color: C.danger }} />
                  <div>
                    <p style={{ margin: 0, fontSize: 11, color: C.danger }}>Total Unpaid</p>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#991B1B' }}>₹{unpaidTotal.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
            {!v.challans?.length ? <EmptyState text="No challans" icon={<CheckCircle size={48} style={{ color: C.success }} />} sub="Clean record." /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {v.challans.map((ch, i) => (
                  <div key={i} style={{ border: `1px solid ${ch.status === 'unpaid' ? C.danger : C.g200}`, borderRadius: 14, padding: 18, background: ch.status === 'unpaid' ? '#FFF5F5' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.g900 }}>{ch.offence}</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: C.g400 }}>{ch.location}</p>
                      </div>
                      <StatusBadge status={ch.status} />
                    </div>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      <div><p style={{ margin: 0, fontSize: 11, color: C.g400 }}>ID</p><p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>{ch.challan_id}</p></div>
                      <div><p style={{ margin: 0, fontSize: 11, color: C.g400 }}>Date</p><p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 600 }}>{ch.challan_date}</p></div>
                      <div><p style={{ margin: 0, fontSize: 11, color: C.g400 }}>Fine</p><p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 800, color: ch.status === 'unpaid' ? C.danger : C.g700 }}>{ch.amount}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'service' && (
          <div>
            <h4 style={{ margin: '0 0 20px', fontSize: 17, fontWeight: 700, color: C.g900 }}>Service History</h4>
            {!v.service_history?.length ? <EmptyState text="No service records yet" /> : (
              <div style={{ position: 'relative', paddingLeft: 28 }}>
                <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: C.g200, borderRadius: 2 }} />
                {v.service_history.map((s, i) => (
                  <div key={i} style={{ position: 'relative', paddingBottom: 24 }}>
                    <div style={{ position: 'absolute', left: -22, top: 4, width: 12, height: 12, borderRadius: '50%', background: i === 0 ? C.primary : C.g300, border: '2px solid #fff', boxShadow: `0 0 0 2px ${i === 0 ? C.primaryLight : C.g200}` }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: C.g800 }}>{s.service_type}</p>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: C.g400 }}>{s.service_date} · {s.odometer} km</p>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: C.g800 }}>{s.cost}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>
    </AppLayout>
  );
};

const EmptyState = ({ text, icon, sub }) => (
  <div style={{ textAlign: 'center', padding: 40 }}>
    {icon || <Settings size={48} style={{ color: C.g300, marginBottom: 12 }} />}
    <p style={{ fontSize: 18, fontWeight: 700, color: C.g800, margin: '12px 0 4px' }}>{text}</p>
    {sub && <p style={{ fontSize: 14, color: C.g400, margin: 0 }}>{sub}</p>}
  </div>
);

export default VehicleDetailPage;
